import { z } from "zod";
import { apiDelete, apiGet, apiPost, apiPut } from "../lib/api";

// ─── Persona state enum ─────────────────────────────────────────────────────────

export const PersonaStateSchema = z.enum([
  "draft",
  "active",
  "retired",
]);
export type PersonaState = z.infer<typeof PersonaStateSchema>;

// ─── Persona summary (list item) ────────────────────────────────────────────────

export const PersonaSummarySchema = z.object({
  persona_id: z.string(),
  display_name: z.string(),
  role: z.string(),
  state: PersonaStateSchema,
  has_voice_ref: z.boolean(),
});
export type PersonaSummary = z.infer<typeof PersonaSummarySchema>;

// ─── Persona record (full detail) ───────────────────────────────────────────────

// Cover identity — the facts the agent may state about itself on a call.
// Rendered into the system prompt at call time by the worker; every field is
// optional, and a persona with none deflects on identity challenges exactly as
// it did before this existed.
export const CoverIdentitySchema = z.object({
  job_title: z.string().nullish(),
  department: z.string().nullish(),
  employee_id: z.string().nullish(),
  tenure: z.string().nullish(),
  manager_name: z.string().nullish(),
  manager_title: z.string().nullish(),
  office_location: z.string().nullish(),
  callback_number: z.string().nullish(),
  monitoring_tool: z.string().nullish(),
  ticket_reference: z.string().nullish(),
});
export type CoverIdentity = z.infer<typeof CoverIdentitySchema>;

export const PersonaRecordSchema = z.object({
  persona_id: z.string(),
  display_name: z.string(),
  role: z.string(),
  bio: z.string().nullable(),
  state: PersonaStateSchema,

  // Synthetic flag
  synthetic: z.boolean(),

  // Voice
  voice_clone_id: z.string().nullable(),
  voice_clone_provider: z.enum(["cartesia", "elevenlabs"]).nullable(),
  tts_speed: z.number().nullable(),
  tts_volume: z.number().nullable(),
  tts_emotion: z.string().nullable(),
  background_ambience_gain: z.number().nullable(),
  language: z.enum(["en", "es", "hi"]).nullish().default("en"),

  // LLM
  llm_model: z.string().nullish().default(null),

  // Cover identity — the facts the agent may state about itself on a call.
  // Rendered into the system prompt at call time by the worker. Every field is
  // optional; a persona with none deflects on identity challenges exactly as
  // before this existed.
  cover_identity: CoverIdentitySchema.nullish().default(null),

  // Email channel
  email_signature: z.string().nullable(),
  from_email_address: z.string().nullable(),

  // SMS
  sms_signature_style: z.string(),

  // Video
  video_avatar_id: z.string().nullable(),
  video_avatar_provider: z.enum(["tavus", "hedra"]).nullable(),

  // System
  system_prompt_override: z.string().nullable(),

  // Consent
  consent_record_uri: z.string().nullable(),
  consent_signed_at: z.string().nullable(),

  // Caller ID
  caller_id_did: z.string().nullable(),

  // Lifecycle
  created_at: z.string(),
  created_by: z.string(),
  retired_at: z.string().nullable(),
  retired_by: z.string().nullable(),
});
export type PersonaRecord = z.infer<typeof PersonaRecordSchema>;

// ─── Response envelopes ─────────────────────────────────────────────────────────

export const PersonaListResponseSchema = z.object({
  items: z.array(PersonaSummarySchema),
  total: z.number(),
});

export const PersonaDetailResponseSchema = z.object({
  record: PersonaRecordSchema,
});

// ─── State machine constants ─────────────────────────────────────────────────────

/**
 * Valid v1 UI transitions for personas. One-way state machine.
 * draft → active → retired. All others produce 409.
 */
export const PERSONA_STATE_TRANSITIONS: Record<PersonaState, PersonaState[]> = {
  draft: ["active"],
  active: ["retired"],
  retired: [],
};

/** Ordered left-to-right for the state machine visualization. */
export const PERSONA_STATE_ORDER: PersonaState[] = ["draft", "active", "retired"];

// ─── API fetchers ───────────────────────────────────────────────────────────────

export function fetchPersonas() {
  return apiGet("/api/personas", PersonaListResponseSchema);
}

export function fetchPersona(id: string) {
  return apiGet(`/api/personas/${id}`, PersonaDetailResponseSchema);
}

export function updatePersona(
  id: string,
  body: Record<string, unknown>,
) {
  return apiPut(`/api/personas/${id}`, PersonaDetailResponseSchema, body);
}

export function createPersona(body: { display_name: string; role: string; bio?: string; synthetic?: boolean }) {
  return apiPost("/api/personas", PersonaDetailResponseSchema, body);
}

export function transitionPersona(
  id: string,
  body: { target_state: PersonaState; comment?: string },
) {
  return apiPost(
    `/api/personas/${id}/transition`,
    PersonaDetailResponseSchema,
    body,
  );
}

/** Hard-delete a persona. 204, or 409 if an active engagement references it. */
export function deletePersona(id: string) {
  return apiDelete(`/api/personas/${id}`);
}
